import 'dayjs/locale/sv';

import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import {
  Button,
  Carousel,
  Link,
  Modal,
  Notification,
  Select,
  Textarea,
  Typography
} from '@/components';
import { useTranslation } from 'react-i18next';
import { useCampaign, useDraftApprove, useDraftReject } from '@/hooks/client';
import { PlatformIcon } from '@/utils/platformIcon';
import { TranslatedLink } from '@/utils/translatedComponents';

import styles from './Draft.module.css';

interface DraftProps {
  draft: Api.Client.Draft;
  refetch: TypeAny;
  campaignId: string;
}

export function Draft({ draft, refetch, campaignId }: DraftProps) {
  const [selectedVersion, setSelectedVersion] = useState<Api.DraftProps | null>(
    draft?.latest_version || null
  );
  const [showModal, setShowModal] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  const { t } = useTranslation();

  const { data: campaignData = {} } = useCampaign(campaignId);
  const memoCampaign = useMemo(() => campaignData || [], [campaignData]);

  const isDecline = showModal === 'decline';

  // Memoize translation calls to prevent instability
  const versionTexts = useMemo(
    () => ({
      version: t('campaignPageDrafts.version'),
      latest: t('campaignPageDrafts.versionLatest').toLowerCase()
    }),
    [t]
  );

  // Memoize the version options to prevent unnecessary re-renders
  const versionOptions = useMemo(() => {
    if (!draft.versions || draft.versions.length <= 1) return [];

    return draft.versions.map((version) => ({
      display: `${versionTexts.version} ${version.version_number}${
        draft.latest_version.version_number === version.version_number
          ? ` (${versionTexts.latest})`
          : ''
      }`,
      value: version.version_number.toString() // Ensure value is always string
    }));
  }, [draft.versions, draft.latest_version.version_number, versionTexts]);

  // Memoize onSuccess callbacks to prevent mutation hook recreation
  const onAcceptSuccess = useMemo(
    () => () => {
      refetch();
      setShowModal(null);
    },
    [refetch]
  );

  const onRejectSuccess = useMemo(
    () => () => {
      refetch();
      setShowModal(null);
    },
    [refetch]
  );

  const { mutate: acceptDraft, isLoading: isAcceptingDraft } = useDraftApprove({
    id: draft.draft_id,
    onSuccess: onAcceptSuccess
  });

  const { mutate: rejectDraft, isLoading: isRejectingDraft } = useDraftReject({
    id: draft.draft_id,
    onSuccess: onRejectSuccess
  });

  function handleSubmit(e: TypeAny) {
    e.preventDefault();

    if (isDecline) {
      rejectDraft(feedback);
    } else {
      acceptDraft(feedback);
    }
  }

  // Memoize carousel content to prevent unnecessary re-renders
  const carouselContent = useMemo(() => {
    if (!selectedVersion?.medias) return [];

    const filtered = selectedVersion.medias.filter((media: DraftMedia) => !media.is_raw);
    const result = filtered.length ? filtered : selectedVersion.medias;

    // Create new array instead of mutating
    return [...result].reverse();
  }, [selectedVersion?.medias]);

  // Memoize the onChange handler to prevent unnecessary re-renders
  const handleVersionChange = useMemo(
    () => (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedVersionNumber = e.target.value;
      const foundVersion = draft.versions.find(
        (version) => version.version_number.toString() === selectedVersionNumber
      );
      if (foundVersion) {
        setSelectedVersion(foundVersion);
      }
    },
    [draft.versions]
  );

  return (
    <>
      {showModal && (
        <Modal handleClose={() => setShowModal(null)}>
          <form className={styles.draftModal} onSubmit={handleSubmit}>
            <Textarea
              fullWidth
              label={t(`draft.modalFeedback.${isDecline ? 'decline' : 'accept'}`)}
              name="feedback"
              onChange={(e) => setFeedback(e.target.value)}
              required={isDecline}
            />

            <Button
              isLoading={isAcceptingDraft || isRejectingDraft}
              theme={isDecline ? 'red' : 'cta'}
              type="submit"
            >
              {t(`campaignPageDrafts.${isDecline ? 'decline' : 'accept'}`)}
            </Button>
          </form>
        </Modal>
      )}

      <div className={styles.draft}>
        {!!selectedVersion?.medias?.length && (
          <div className={styles.draftImage}>
            {draft.versions.length > 1 && (
              <div className={styles.draftImageVersions}>
                <Select
                  name="draftVersion"
                  options={versionOptions}
                  onChange={handleVersionChange}
                  selected={selectedVersion?.version_number}
                />
              </div>
            )}
            <Carousel
              content={carouselContent}
              // content={selectedVersion.medias.filter(
              //   (media: DraftMedia) => media.is_raw === (draft.beat.beat_type === 'IGC')
              // )}
            />
          </div>
        )}

        <div className={styles.draftContent}>
          <div>
            {selectedVersion?.feedback && (
              <div className={styles.draftContentInfoFeedback}>
                <Notification theme={selectedVersion.status === 'REJECTED' ? 'error' : 'info'}>
                  {selectedVersion.status === 'REJECTED' && (
                    <Typography sizes={['bold']}>{`${t('translatedType.REJECTED')}: `}</Typography>
                  )}
                  <span
                    className={styles.draftContentInfoFeedbackHtml}
                    dangerouslySetInnerHTML={{
                      __html: selectedVersion.feedback.replace(/\n/g, '<br />')
                    }}
                  />
                </Notification>
              </div>
            )}

            <div className={styles.draftContentMugshot}>
              <div
                className={styles.draftContentMugshotSrc}
                style={{ backgroundImage: `url('${draft.influencer?.mugshot_url}')` }}
              />

              <Link href={draft.influencer.permalink}>{draft.influencer?.name}</Link>
            </div>

            <div className={styles.draftContentInfo}>
              <p className={styles.draftContentInfoBeat}>
                <span className={styles.draftContentInfoBeatPlatform}>
                  {PlatformIcon(draft.beat.platform)}
                </span>

                <TranslatedLink
                  href={`/connect/campaigns/${campaignId}/beat/${draft.beat.beat_id}`}
                >
                  {draft.product_name}
                </TranslatedLink>
              </p>

              {draft.date_publish && (
                <p>
                  <Typography sizes={['label']}>
                    {`${t('campaignPageDrafts.headingDue')}: ${dayjs(draft.date_publish).format('YYYY-MM-DD')}`}
                  </Typography>
                </p>
              )}

              {draft.date_draft && (
                <p>
                  <Typography sizes={['label']}>
                    {`${t('campaignPageDrafts.headingDraft')}: ${dayjs(draft.date_draft).format('YYYY-MM-DD')}`}
                  </Typography>
                </p>
              )}

              {selectedVersion?.caption && (
                <p>
                  <Typography sizes={['label']}>
                    {`${t('campaignPageDrafts.headingCaption')}: `}
                  </Typography>
                  <br />
                  <strong
                    style={{ overflowWrap: 'anywhere' }}
                    dangerouslySetInnerHTML={{
                      __html: selectedVersion.caption.replace(/\n/g, '<br />')
                    }}
                  />
                </p>
              )}
            </div>
          </div>

          {draft.latest_version.status !== 'APPROVED' && (
            <div className={styles.draftContentButtons}>
              <Button
                disabled={isRejectingDraft || memoCampaign.is_demo}
                onClick={() => setShowModal('decline')}
                theme="red"
              >
                {t('campaignPageDrafts.decline')}
              </Button>

              <Button
                isLoading={isAcceptingDraft}
                disabled={isRejectingDraft || memoCampaign.is_demo}
                onClick={() => setShowModal('accept')}
                theme="cta"
              >
                {t('campaignPageDrafts.accept')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
